import { getAssetLinkFromStorage } from 'api/Firebase/storage'
import { VISITOR } from 'app/routes'
import { Container } from 'components/Container'
import { LanguageSwitcher } from 'components/LanguageSwitcher'
import QRCode from 'react-qr-code'
import React, { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { usePlace } from 'store/usePlace'
import { useKioskSession } from 'store/useKioskSession'
import FormattedMessage from 'utils/FormattedMessage'

function darkenHex(hex: string, amount = 45): string {
  const clean = hex.replace('#', '')
  const num = parseInt(
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean,
    16
  )
  const r = Math.max(0, (num >> 16) - amount)
  const g = Math.max(0, ((num >> 8) & 0xff) - amount)
  const b = Math.max(0, (num & 0xff) - amount)
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`
}

function isNearWhite(hex: string): boolean {
  const clean = hex.replace('#', '')
  const expanded =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean
  if (expanded.length !== 6) return false
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return r > 240 && g > 240 && b > 240
}

function buildBgStyle(color?: string): React.CSSProperties | undefined {
  if (!color) return undefined
  if (isNearWhite(color)) return { background: color }
  return { background: `linear-gradient(58.72deg, ${color} 7.71%, ${darkenHex(color)} 90.58%)` }
}

export const KioskPage = () => {
  const { placeId: urlPlaceId } = useParams()
  const [searchParams] = useSearchParams()
  const kioskCode = searchParams.get('kioskCode') ?? undefined
  const { place } = usePlace()
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (kioskCode) useKioskSession.getState().setKioskCode(kioskCode)
  }, [kioskCode])

  useEffect(() => {
    if (!urlPlaceId) return
    usePlace.getState().subscribeToPlace(urlPlaceId)
    return () => usePlace.getState().unsubscribeFromPlace()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPlaceId])

  const kiosk = place?.customisation?.kiosk ?? {}
  const isExtended = kiosk.layout === 'extended'
  const bgStyle = buildBgStyle(kiosk.backgroundColor)
  const buttonColorStyle = kiosk.buttonColor ? { backgroundColor: kiosk.buttonColor } : undefined

  useEffect(() => {
    if (!isExtended || !place?.logo) {
      setLogoUrl(undefined)
      return
    }
    getAssetLinkFromStorage(place.logo)
      .then(link => setLogoUrl(link))
      .catch(() => setLogoUrl(undefined))
  }, [place?.logo, isExtended])

  if (isExtended) {
    return (
      <div className='relative kiosk-bg-gray h-screen flex flex-col' style={bgStyle}>
        <div className='absolute right-7 top-7 dark'>
          <LanguageSwitcher />
        </div>
        <Container>
          <div className='flex flex-col items-center justify-center h-screen gap-5'>
            {logoUrl && (
              <img
                src={logoUrl}
                alt='logo'
                className='w-20 h-20 md:w-28 md:h-28 object-contain rounded-full'
              />
            )}
            <h1 className='text-3xl md:text-4xl font-semibold text-white text-center'>
              {kiosk.welcomeText || <FormattedMessage id='kiosk.title' />}
            </h1>
            <p className='text-lg md:text-xl font-normal text-white text-center max-w-[70%]'>
              {kiosk.instructionsText || <FormattedMessage id='kiosk.description' />}
            </p>
            <div className='flex items-center justify-around w-full mt-4 gap-6'>
              <div className='w-[130px] h-[130px] md:w-[180px] md:h-[180px] lg:w-[220px] lg:h-[220px] rounded-[10px] bg-white flex items-center justify-center p-4 flex-shrink-0'>
                <QRCode
                  id='qr-code'
                  size={512}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                  value={`https://book.${process.env.NEXT_PUBLIC_DOMAIN}/places/${urlPlaceId}/book`}
                  viewBox={`0 0 512 512`}
                />
              </div>
              <Link
                to={{
                  pathname: urlPlaceId && VISITOR.BOOK.replace(':placeId', urlPlaceId),
                }}
                state={{ kioskMode: 'on', kioskCode }}
              >
                <button
                  type='button'
                  className='rounded-[10px] bg-swatch-100 w-[220px] h-[130px] md:w-[300px] md:h-[180px] lg:w-[360px] lg:h-[220px] px-3.5 py-2.5 text-4xl md:text-5xl font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                  style={buttonColorStyle}
                >
                  <FormattedMessage id='book' />
                </button>
              </Link>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className='relative kiosk-bg-gray h-screen flex justify-center flex-col' style={bgStyle}>
      {/* <div className='absolute top-0 w-full h-[13px] bg-swatch-100'></div> */}
      {/* <div className='absolute bottom-0 w-full h-[13px] bg-swatch-100'></div> */}
      <Container>
        <div className='absolute right-7 top-7 dark'>
          <LanguageSwitcher />
        </div>
        <div className='flex flex-col justify-center h-full'>
          <div className='w-full flex justify-center'>
            <div className='flex items-center flex-col justify-center max-w-[90%] md:max-w-[70%]'>
              <h1 className='text-4xl font-semibold text-white mb-5 text-center'>
                {kiosk.welcomeText || <FormattedMessage id='kiosk.title' />}
              </h1>
              <p className='text-xl font-normal text-white text-center mb-5'>
                {kiosk.instructionsText || <FormattedMessage id='kiosk.description' />}
              </p>
            </div>
          </div>

          <div className='flex justify-around mt-5 items-start'>
            <div className='flex flex-col items-center gap-5'>
              <div className='w-[210px] h-[210px] md:w-[310px] md:h-[310px] lg:w-[380px] lg:h-[380px] rounded-[10px] bg-white flex items-center justify-center p-6'>
                <QRCode
                  id='qr-code'
                  size={512}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                  value={`https://book.${process.env.NEXT_PUBLIC_DOMAIN}/places/${urlPlaceId}/book`}
                  viewBox={`0 0 512 512`}
                />
              </div>
              <p className='text-4xl font-semibold text-white text-center'>
                <FormattedMessage id='kiosk.scan' />
              </p>
            </div>
            <Link
              to={{
                pathname: urlPlaceId && VISITOR.BOOK.replace(':placeId', urlPlaceId),
              }}
              state={{ kioskMode: 'on', kioskCode }}
            >
              <button
                type='button'
                className='rounded-[10px] bg-swatch-100 w-[210px] h-[210px] md:w-[310px] md:h-[310px] lg:w-[380px] lg:h-[380px] px-3.5 py-2.5 text-5xl font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                style={buttonColorStyle}
              >
                <FormattedMessage id='book' />
              </button>
            </Link>
          </div>
        </div>
      </Container>
    </div>
  )
}
